import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchVaultLinks, fetchVaultPageSummaries, fetchVaultRuns } from '@/lib/librarian/api';
import type { KnowledgeBaseVaultLink, KnowledgeBaseVaultPage, KnowledgeBaseVaultRun } from '@/lib/librarian/types';
import { toast } from '@/components/ui/use-toast';

export function useKnowledgeBaseVaultPages(vaultId?: string | null) {
  const [pages, setPages] = useState<KnowledgeBaseVaultPage[]>([]);
  const [links, setLinks] = useState<KnowledgeBaseVaultLink[]>([]);
  const [runs, setRuns] = useState<KnowledgeBaseVaultRun[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!vaultId) {
      setPages([]);
      setLinks([]);
      setRuns([]);
      return;
    }

    setLoading(true);
    try {
      const [nextPages, nextLinks, nextRuns] = await Promise.all([
        fetchVaultPageSummaries(vaultId),
        fetchVaultLinks(vaultId),
        fetchVaultRuns(vaultId),
      ]);
      setPages(nextPages);
      setLinks(nextLinks);
      setRuns(nextRuns);
    } catch (error) {
      console.error('Failed to fetch vault pages:', error);
      toast({ title: 'Error', description: 'Failed to load vault pages.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [vaultId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const pageById = useMemo(() => new Map(pages.map((page) => [page.id, page])), [pages]);
  const linkCountByPageId = useMemo(() => {
    const counts = new Map<string, number>();
    links.forEach((link) => {
      counts.set(link.from_page_id, (counts.get(link.from_page_id) ?? 0) + 1);
      if (link.to_page_id) {
        counts.set(link.to_page_id, (counts.get(link.to_page_id) ?? 0) + 1);
      }
    });
    return counts;
  }, [links]);

  return {
    pages,
    links,
    runs,
    loading,
    refresh,
    pageById,
    linkCountByPageId,
  };
}